import React, { useState, useEffect } from 'react';
import { Github, Linkedin, Mail, ExternalLink, Code, Palette, Database, ArrowUp } from 'lucide-react';

const Portfolio = () => {
  const [isVisible, setIsVisible] = useState({});
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: entry.isIntersecting
            }));
          });
        },
        { threshold: 0.1 }
    );

    document.querySelectorAll('[id]').forEach((el) => observer.observe(el));

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const skills = [
    {
      category: 'Core Java',
      items: ['Java 17', 'OOP', 'Maven', 'JUnit', 'Mockito', 'Test-Driven Development'],
      icon: Code,
      color: 'from-blue-400 to-purple-600'
    },
    {
      category: 'Backend & Frameworks',
      items: ['Spring Boot', 'Spring Security', 'REST APIs', 'JWT Authentication', 'Spring Scheduler'],
      icon: Palette,
      color: 'from-green-400 to-blue-600'
    },
    {
      category: 'Databases & DevOps',
      items: ['MongoDB', 'MySQL', 'PostgreSQL', 'Docker', 'AWS EC2', 'Git'],
      icon: Database,
      color: 'from-pink-400 to-red-600'
    }
  ];

  const projects = [
    {
      title: 'Job Offers',
      description: 'Job Offers Project is a web application built for users to find, ' +
          'browse, and add Java job offers. Users must be authenticated to interact with offers. ' +
          'The application periodically fetches fresh job offers from an external API.',
      tech: ['Java 17', 'Spring Boot', 'Spring Data JPA', 'MongoDB', 'REST API', 'Docker', 'Scheduled Tasks', 'AWS', 'Testcontainers', 'TDD'],
      image: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=400&h=250&fit=crop',
      link: 'https://joboffers.pkasiewicz.pl/'
    },
    {
      title: 'Lotto Game',
      description: 'Backend lottery system built with Hexagonal Architecture and Domain-Driven Design, ' +
          'split into four independent modules: number receiver, number generator, result checker, and result announcer. ' +
          'Users submit 6 numbers (1–99) and receive results after automated Saturday draws. ' +
          'Winning numbers are fetched from an external API via a scheduled job, results are computed by comparing ' +
          'all submitted tickets, and hit statistics are cached for instant retrieval.',
      tech: ['Java 21', 'Spring Boot', 'Spring Data JPA', 'PostgreSQL', 'Flyway', 'REST API', 'Docker', 'Spring Scheduler', 'Testcontainers', 'WireMock', 'TDD'],
      image: 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?w=400&h=250&fit=crop',
      link: 'https://lotto.pkasiewicz.pl/'
    }
    // {
    //   title: 'Web Development Portfolio',
    //   description: 'Modern portfolio website showcasing my journey to becoming a Java developer',
    //   tech: ['React', 'Tailwind CSS', 'Vercel'],
    //   image: 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=400&h=250&fit=crop',
    //   link: '#'
    // }
  ];

  return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white overflow-x-hidden scroll-smooth">
        {/* Navigation */}
        <nav className="fixed top-0 w-full z-50 bg-black bg-opacity-20 border-b border-gray-700">
          <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
            <div className="text-2xl font-bold text-blue-400">
              Portfolio
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="#about" className="hover:text-blue-400 transition-colors scroll-smooth">About</a>
              <a href="#skills" className="hover:text-blue-400 transition-colors scroll-smooth">Skills</a>
              <a href="#projects" className="hover:text-blue-400 transition-colors scroll-smooth">Projects</a>
              <a href="#contact" className="hover:text-blue-400 transition-colors scroll-smooth">Contact</a>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section id="hero" className="min-h-screen flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 animate-pulse"></div>
          <div className="text-center z-10 max-w-4xl mx-auto px-6">
            <div className={`transform transition-all duration-1000 ${isVisible.hero ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <h1 className="text-6xl md:text-8xl font-bold mb-6">
              <span className="text-blue-400 animate-pulse">
                Piotr Kasiewicz
              </span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-gray-300">
                Aspiring Java Developer
              </p>
              <div className="flex justify-center space-x-6 mb-12">
                <a href="https://github.com/pkasiewicz" target="_blank" rel="noopener noreferrer" className="p-3 bg-white bg-opacity-10 rounded-full hover:bg-white hover:bg-opacity-20 transition-all hover:scale-110">
                  <Github className="w-6 h-6" />
                </a>
                <a href="https://linkedin.com/in/piotr-kasiewicz" target="_blank" rel="noopener noreferrer" className="p-3 bg-white bg-opacity-10 rounded-full hover:bg-white hover:bg-opacity-20 transition-all hover:scale-110">
                  <Linkedin className="w-6 h-6" />
                </a>
                <a href="mailto:piotr.kasiewicz@example.com" target="_blank" rel="noopener noreferrer" className="p-3 bg-white bg-opacity-10 rounded-full hover:bg-white hover:bg-opacity-20 transition-all hover:scale-110">
                  <Mail className="w-6 h-6" />
                </a>
              </div>
              <a
                  href="#contact"
                  className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full text-white font-semibold hover:shadow-2xl hover:shadow-blue-500 hover:shadow-opacity-25 transition-all duration-300 hover:scale-105"
              >
                Contact Me
              </a>
            </div>
          </div>

          {/* Floating geometric shapes */}
          <div className="absolute top-20 left-10 w-20 h-20 bg-blue-500/30 rounded-full animate-bounce"></div>
          <div className="absolute top-40 right-20 w-16 h-16 bg-purple-500/30 rounded-lg rotate-45 animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-12 h-12 bg-pink-500/30 rounded-full animate-ping"></div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className={`transform transition-all duration-1000 delay-200 ${isVisible.about ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                About Me
              </h2>
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="space-y-6">
                  <p className="text-lg text-gray-300 leading-relaxed">
                    Hi! I'm Piotr, a passionate Java developer who has always been fascinated by
                    technology. Since my school days, I've been developing my skills in web development
                    and building community forums.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed">
                    Working on game servers taught me teamwork and project management. I recently
                    completed JavaStart and JuniorJavaReady courses, and I'm actively building
                    my portfolio to become a professional Java developer.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed">
                    In my free time, I maintain a healthy lifestyle through regular gym workouts
                    and cycling. I also love watching good movies and continuously learning new technologies.
                  </p>
                </div>
                <div className="relative">
                  <div className="w-80 h-80 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-full p-1">
                    <div className="w-full h-full bg-slate-900 rounded-full flex items-center justify-center">
                      <div className="text-6xl">👨‍💻</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Skills Section */}
        <section id="skills" className="py-20 px-6 bg-black/20">
          <div className="max-w-6xl mx-auto">
            <div className={`transform transition-all duration-1000 delay-300 ${isVisible.skills ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                Skills & Expertise
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {skills.map((skill, index) => {
                  const Icon = skill.icon;
                  return (
                      <div
                          key={skill.category}
                          className={`group p-8 bg-gray-900 bg-opacity-50 rounded-2xl border border-gray-600 hover:border-gray-400 transition-all duration-500 hover:scale-105 transform ${isVisible.skills ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                          style={{ transitionDelay: `${index * 100}ms` }}
                      >
                        <div className={`w-16 h-16 mx-auto mb-6 bg-gradient-to-r ${skill.color} rounded-2xl flex items-center justify-center group-hover:rotate-12 transition-transform duration-300`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-center mb-4">{skill.category}</h3>
                        <ul className="text-gray-400 text-center space-y-2">
                          {skill.items.map((item, i) => (
                              <li key={i} className="text-sm">{item}</li>
                          ))}
                        </ul>
                      </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section id="projects" className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className={`transform transition-all duration-1000 delay-400 ${isVisible.projects ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                Featured Projects
              </h2>
              <div className="flex flex-wrap justify-center gap-8">
                {projects.map((project, index) => (
                    <div
                        key={project.title}
                        className={`text-center w-80 group bg-gray-900 bg-opacity-50 rounded-2xl overflow-hidden border border-gray-600 hover:border-gray-400 transition-all duration-500 hover:scale-105 transform ${isVisible.projects ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                        style={{ transitionDelay: `${index * 150}ms` }}
                    >
                      <div className="relative overflow-hidden">
                        <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-bold mb-3 group-hover:text-blue-400 transition-colors">
                          {project.title}
                        </h3>
                        <p className="text-gray-400 mb-4">{project.description}</p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {project.tech.map((tech) => (
                              <span
                                  key={tech}
                                  className="px-3 py-1 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-full text-sm border border-blue-500/30"
                              >
                          {tech}
                        </span>
                          ))}
                        </div>
                        <a
                            href={project.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors group"
                        >
                          View Project
                          <ExternalLink className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </a>
                      </div>
                    </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 px-6 bg-black/20">
          <div className="max-w-4xl mx-auto text-center">
            <div className={`transform transition-all duration-1000 delay-500 ${isVisible.contact ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <h2 className="text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                Let's Work Together
              </h2>
              <p className="text-xl text-gray-300 mb-12">
                Ready to bring your ideas to life? Let's create something amazing together.
              </p>
              <a
                  href="mailto:piotr.kasiewicz@example.com"
                  className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full text-white font-semibold hover:shadow-2xl hover:shadow-blue-500 hover:shadow-opacity-25 transition-all duration-300 hover:scale-105"
              >
                Get In Touch
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 border-t border-white/10">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <p className="text-gray-400">
              © 2025 Piotr Kasiewicz. Built with React & Tailwind CSS
            </p>
          </div>
        </footer>

        {/* Scroll to top button */}
        {showScrollTop && (
            <button
                onClick={scrollToTop}
                className="fixed bottom-8 right-8 p-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full text-white shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 z-50"
            >
              <ArrowUp className="w-6 h-6" />
            </button>
        )}
      </div>
  );
};

export default Portfolio;